import { useMutation } from '@apollo/client';
import { CheckIcon } from '@heroicons/react/20/solid';
import { yupResolver } from '@hookform/resolvers/yup';
import { useToast } from '@retainerclub/hooks';
import { CREATE_WARRANTY } from '@retainerclub/shared-api';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ErrorMessage } from '../../../shared';
import { initialValues, schema } from './helpers';
import StepFour from './steps/StepFour';
import StepOne from './steps/StepOne';
import StepSuccess from './steps/StepSuccess';
import StepThree from './steps/StepThree';
import StepTwo from './steps/StepTwo';

const steps = [
  { id: 1, name: 'Retainer Info', component: StepOne },
  { id: 2, name: 'Issue Details', component: StepTwo },
  { id: 3, name: 'Additional Info', component: StepThree },
  { id: 4, name: 'Review & Submit', component: StepFour },
  { id: 5, name: 'Success', component: StepSuccess },
];

function WarrantyFormInner({
  productionOrder,
  isModal = false,
  onClose,
  refetch = null,
}) {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [mutationErrors, setMutationErrors] = useState(null);
  const closeModalTimeoutRef = useRef(null);

  useEffect(() => {
    return function cleanup() {
      if (closeModalTimeoutRef.current) {
        clearTimeout(closeModalTimeoutRef.current);
      }
    };
  }, []);

  const [createWarranty, { error, loading }] = useMutation(CREATE_WARRANTY);
  const { setToast } = useToast();

  const {
    register,
    handleSubmit,
    formState,
    control,
    getValues,
    setValue,
    trigger,
    ...rest
  } = useForm({
    resolver: yupResolver(schema()),
    defaultValues: initialValues({ productionOrder }),
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });

  async function handleNext() {
    // Define fields to validate for each step
    const stepFields = {
      1: ['scanStatus', 'haveCurrentSet', 'howOftenWear'],
      2: ['userIssue', 'arch', 'upperArchFit', 'lowerArchFit'],
      3: ['message', 'warrantyImagesAttributes'],
      4: ['whereToShip'], // Shipping preference validation
    };

    // Validate only the fields relevant to the current step
    const fieldsToValidate = stepFields[currentStep] || [];
    const isValid =
      fieldsToValidate.length === 0 ? true : await trigger(fieldsToValidate);

    if (isValid && currentStep < steps.length) {
      // Save current step data
      const currentData = getValues();
      setFormData({ ...formData, ...currentData });
      setCurrentStep(currentStep + 1);
    }
  }

  function handlePrevious() {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }

  function handleStepClick(stepId) {
    // Only allow going back or to current step, but not to success step unless we're already there
    if (stepId <= currentStep && stepId !== 5) {
      setCurrentStep(stepId);
    } else if (stepId === 5 && currentStep === 5) {
      // Allow clicking on success step if we're already on it
      setCurrentStep(stepId);
    }
  }

  async function submitRecord(values) {
    try {
      setMutationErrors(null);

      const params = {
        variables: {
          input: {
            input: { ...values },
            clientMutationId: crypto.randomUUID(),
          },
        },
      };

      const { data } = await createWarranty(params);

      const mErrors = data.createWarranty.errorMessages;

      if (mErrors.length > 0) {
        setMutationErrors(mErrors);
      } else {
        setMutationErrors(null);

        // Show success toast
        setToast({
          show: true,
          title: 'Success',
          message: 'Warranty request submitted successfully',
          type: 'success',
        });

        // Refetch is best-effort; warranty was created successfully
        if (refetch) {
          try {
            await refetch();
          } catch (refetchErr) {
            console.error('Refetch after warranty submit failed:', refetchErr);
          }
        }

        // Navigate to success step
        setCurrentStep(5);

        // In modal, close after a short delay so user sees success then updated table
        if (isModal && onClose) {
          closeModalTimeoutRef.current = setTimeout(() => onClose(), 1500);
        }
      }
    } catch (err) {
      console.error('Error submitting warranty:', err);
      setMutationErrors([
        { message: 'An unexpected error occurred. Please try again.' },
      ]);
    }
  }

  const visibleSteps = steps.filter((step) => step.id !== 5);
  const progressPercentage =
    currentStep === 5
      ? 100
      : ((currentStep - 1) / (visibleSteps.length - 1)) * 100;

  const CurrentStepComponent = steps[currentStep - 1].component;

  return (
    <div
      className={`${isModal ? 'mt-4' : 'mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8'}`}
    >
      <div className="rounded-lg bg-white shadow">
        {/* Progress Header */}
        <div className="px-6 py-6">
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-gray-700 text-sm">
                Step {currentStep === 5 ? visibleSteps.length : currentStep} of{' '}
                {visibleSteps.length}
              </h3>
              <span className="text-gray-500 text-sm">
                {Math.round(progressPercentage)}% Complete
              </span>
            </div>
            {/* Progress Bar */}
            <div className="mt-2">
              <div className="h-2 overflow-hidden rounded-full bg-gray-200">
                <div
                  style={{ width: `${progressPercentage}%` }}
                  className="h-full rounded-full bg-green-500 transition-all duration-500"
                />
              </div>
            </div>
          </div>

          {/* Step Indicators */}
          <nav aria-label="Progress" className="mt-4 mb-0">
            <div className="flex justify-center">
              <ol className="flex items-center justify-center space-x-5 sm:space-x-6 lg:space-x-1">
                {steps
                  .filter((step) => step.id !== 5)
                  .map((step, stepIdx) => (
                    <li key={step.name} className="flex items-center">
                      <button
                        type="button"
                        onClick={() => {
                          handleStepClick(step.id);
                        }}
                        className="group relative flex flex-col items-center md:flex-row"
                        disabled={step.id > currentStep}
                      >
                        <span
                          className={`${
                            step.id < currentStep
                              ? 'bg-green-500'
                              : step.id === currentStep
                                ? 'border-2 border-green-500 bg-white'
                                : 'bg-gray-200'
                          } relative z-10 flex h-8 w-8 items-center justify-center rounded-full transition-all duration-300 lg:h-10 lg:w-10`}
                        >
                          {step.id < currentStep ? (
                            <CheckIcon
                              className="h-4 w-4 text-white lg:h-5 lg:w-5"
                              aria-hidden="true"
                            />
                          ) : (
                            <span
                              className={`${
                                step.id === currentStep
                                  ? 'text-green-500'
                                  : 'text-gray-600'
                              } font-medium text-xs lg:text-sm`}
                            >
                              {step.id}
                            </span>
                          )}
                        </span>
                        <span
                          className={`${
                            step.id === currentStep
                              ? 'font-medium text-gray-900'
                              : step.id < currentStep
                                ? 'text-gray-700'
                                : 'text-gray-400'
                          } absolute -bottom-6 whitespace-nowrap text-center text-xs md:relative md:bottom-auto md:ml-3 lg:text-sm`}
                        >
                          {step.name}
                        </span>
                      </button>
                      {/* Connector line between steps */}
                      {stepIdx < visibleSteps.length - 1 && (
                        <div className="ml-5 sm:ml-6 lg:ml-4 xl:ml-6">
                          <div className="h-0.5 w-9 bg-gray-200 sm:w-12 lg:w-8 xl:w-12">
                            <div
                              className={`h-0.5 bg-green-500 transition-all duration-500 ${
                                currentStep > step.id ? 'w-full' : 'w-0'
                              }`}
                            />
                          </div>
                        </div>
                      )}
                    </li>
                  ))}
              </ol>
            </div>
          </nav>
        </div>
        <div className="border-gray-200 border-t" />

        {/* Form Content */}
        <FormProvider
          {...rest}
          control={control}
          register={register}
          handleSubmit={handleSubmit}
          formState={formState}
          getValues={getValues}
          setValue={setValue}
          trigger={trigger}
        >
          <form onSubmit={(e) => e.preventDefault()} noValidate>
            <div className="p-6">
              {/* Error Display */}
              <ErrorMessage error={error} errorMessages={mutationErrors} />

              <CurrentStepComponent
                productionOrder={productionOrder}
                onClose={onClose}
              />
            </div>

            {/* Navigation Buttons - Hide on success step */}
            {currentStep !== 5 && (
              <div className="mt-16 flex justify-between rounded-b-lg border-gray-200 border-t bg-gray-50 px-6 py-4">
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  className={`${
                    currentStep === 1
                      ? 'cursor-not-allowed bg-gray-100 text-gray-400'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } rounded-md border border-gray-300 px-4 py-2 font-medium text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2`}
                >
                  Previous
                </button>

                {currentStep === 4 ? (
                  <button
                    type="button"
                    onClick={handleSubmit(submitRecord)}
                    disabled={loading}
                    className={`${
                      loading
                        ? 'cursor-not-allowed bg-gray-400'
                        : 'bg-green-600 hover:bg-green-700'
                    } rounded-md border border-transparent px-4 py-2 font-medium text-sm text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2`}
                  >
                    {loading ? 'Submitting...' : 'Submit Warranty'}
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleNext}
                    className="rounded-md border border-transparent bg-green-600 px-4 py-2 font-medium text-sm text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    Next
                  </button>
                )}
              </div>
            )}
          </form>
        </FormProvider>
      </div>
    </div>
  );
}

WarrantyFormInner.propTypes = {
  productionOrder: PropTypes.object.isRequired,
  isModal: PropTypes.bool,
  onClose: PropTypes.func,
  refetch: PropTypes.func,
};

export default WarrantyFormInner;
